import React, { createContext, useContext, useState, ReactNode } from "react";

interface Product {
  name: string;
  quantity: number;
  amount: number;
  currency: string;
  description?: string;
  totalPrice?: number;
}

interface Customer {
  email?: string;
  phone?: string;
  name: string;
  id?: string;
  address?: string;
  city?: string;
  country?: string;
}

export interface CheckoutData {
  orderId: string;
  merchantId: string;
  orderAmount: number;
  orderCurrency: string;
  transactionDate: string;
  products: Product[];
  customer?: Customer;
  edfaMerchantId?: string;
  shipping?: string;
  discount?: string;
  subTotal?: string;
  taxVATAmount?: string;
  subTotalWithVat?: string;
  totalItems?: number;
  sendBy?: string;
}

interface CheckoutContextType {
  checkoutData: CheckoutData | null;
  setCheckoutData: (data: CheckoutData | null) => void;
  clearCheckoutData: () => void;
}

const CheckoutContext = createContext<CheckoutContextType | undefined>(
  undefined
);

export const CheckoutProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [checkoutData, setCheckoutDataState] = useState<CheckoutData | null>(
    () => {
      // Try to load from sessionStorage on initialization
      try {
        const savedData = sessionStorage.getItem("checkoutData");
        return savedData ? JSON.parse(savedData) : null;
      } catch (error) {
        console.error(
          "Error loading checkout data from sessionStorage:",
          error
        );
        return null;
      }
    }
  );

  const setCheckoutData = (data: CheckoutData | null) => {
    setCheckoutDataState(data);
    if (data) {
      try {
        sessionStorage.setItem("checkoutData", JSON.stringify(data));
      } catch (error) {
        console.error("Error saving checkout data to sessionStorage:", error);
      }
    } else {
      sessionStorage.removeItem("checkoutData");
    }
  };

  const clearCheckoutData = () => {
    setCheckoutDataState(null);
    sessionStorage.removeItem("checkoutData");
  };

  return (
    <CheckoutContext.Provider
      value={{ checkoutData, setCheckoutData, clearCheckoutData }}
    >
      {children}
    </CheckoutContext.Provider>
  );
};

export const useCheckout = () => {
  const context = useContext(CheckoutContext);
  if (context === undefined) {
    throw new Error("useCheckout must be used within a CheckoutProvider");
  }
  return context;
};
